import supabase from '../integrations/supabase';

/**
 * API Service for communicating with the FastAPI backend
 * Handles authentication, error handling, and request formatting
 */

class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    this.apiVersion = '/api/v1';
  }

  getAuthToken() {
    // Get token from localStorage
    const localToken = localStorage.getItem('user_token');
    
    // For development/testing, use test token if no real token is available
    if (!localToken && process.env.NODE_ENV === 'development') {
      return 'test-token';
    }
    return localToken;
  }

  async request(endpoint, config = {}) {
    const url = `${this.baseURL}${this.apiVersion}${endpoint}`;
    const method = config.method || 'GET';
    const headers = {
      'Content-Type': 'application/json',
      ...config.headers
    };

    // Add authorization header if we have a token
    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      // Also add x_user_token header for backend compatibility
      headers['x_user_token'] = token;
    }

    const requestConfig = {
      method,
      headers,
      ...config
    };

    // Remove Content-Type for FormData requests
    if (config.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    // Add body for non-GET requests
    if (config.body && requestConfig.method !== 'GET') {
      if (config.body instanceof FormData) {
        requestConfig.body = config.body;
      } else {
        requestConfig.body = JSON.stringify(config.body);
      }
    }

    

    try {
      const response = await fetch(url, requestConfig);
      
      // Handle network errors
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ API Error ${response.status}:`, errorText);
        
        // Handle specific error cases
        if (response.status === 401) {
          // Unauthorized - clear tokens and redirect to login
          localStorage.removeItem('user_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
          throw new Error('Authentication required');
        }
        
        if (response.status === 403) {
          throw new Error('Access denied');
        }
        
        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
        
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      const data = await response.json();
      
      // Dispatch backend success event for global error handling
      window.dispatchEvent(new CustomEvent('backend-success', {
        detail: {
          type: 'BACKEND_SUCCESS',
          endpoint: endpoint,
          timestamp: Date.now()
        }
      }));
      
      return data;
    } catch (error) {
      console.error('❌ API request failed:', error);
      
      // Handle network errors specifically
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        console.error('🌐 Network error - server may be down or CORS issue');
        
        // Dispatch backend error event for global error handling
        window.dispatchEvent(new CustomEvent('backend-error', {
          detail: {
            type: 'BACKEND_ERROR',
            error: error.message,
            endpoint: endpoint,
            timestamp: Date.now()
          }
        }));
        
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      throw error;
    }
  }

  // Study AI endpoints (AI-generated content)
  async generateStudyContent(subject, chapter) {
    return this.request('/study-ai/study-ai/generate', {
      method: 'POST',
      body: { subject, chapter }
    });
  }

  async regenerateStudyContent(subject, chapter) {
    return this.request('/study-ai/study-ai/regenerate', {
      method: 'POST',
      body: { subject, chapter }
    });
  }

  async generateFlashcards(subject, chapter, numItems = 10) {
    return this.request('/study-ai/study-ai/flashcards', {
      method: 'POST',
      body: { subject, chapter, num_items: numItems }
    });
  }

  async generateQuiz(subject, chapter, numItems = 10) {
    return this.request('/study-ai/study-ai/quiz', {
      method: 'POST',
      body: { subject, chapter, num_items: numItems }
    });
  }

  // Study Database endpoints (existing content)
  async getExistingContent(contentType, subject, chapter, page = 1, perPage = 10) {
    return this.request(`/study/content/${contentType}?subject=${encodeURIComponent(subject)}&chapter=${encodeURIComponent(chapter)}&page=${page}&per_page=${perPage}`);
  }

  async relearnContent(contentType, contentId) {
    return this.request(`/study/relearn/${contentType}?content_id=${contentId}`);
  }

  // Subject-level AI endpoints (AI-generated content)
  async generateSubjectFlashcards(subject, numItems = 25, bookName = '') {
    return this.request('/study-ai/study-ai/subject/flashcards', {
      method: 'POST',
      body: { subject, num_items: numItems, book_name: bookName }
    });
  }

  async generateSubjectQuiz(subject, numItems = 25, bookName = '') {
    return this.request('/study-ai/study-ai/subject/quiz', {
      method: 'POST',
      body: { subject, num_items: numItems, book_name: bookName }
    });
  }

  async generateChapters(subject, classLevel = '', bookName = '') {
    return this.request('/study/chapters', {
      method: 'POST',
      body: { subject, class_level: classLevel, book_name: bookName }
    });
  }

  // Subject-level Database endpoints (existing content)
  async getExistingSubjectContent(contentType, subject, page = 1, perPage = 5, bookName = '') {
    const params = new URLSearchParams({
      subject: subject,
      page: page.toString(),
      per_page: perPage.toString()
    });
    
    if (bookName) {
      params.append('book_name', bookName);
    }
    
    return this.request(`/study/subject/content/${contentType}?${params.toString()}`);
  }

  async relearnSubjectContent(contentType, contentId) {
    return this.request(`/study/subject/relearn/${contentType}?content_id=${contentId}`);
  }

  // Content retrieval by ID endpoints (from database)
  async getStudyContentById(contentId) {
    return this.request(`/study/relearn/study?content_id=${contentId}`);
  }

  async getFlashcardsById(contentId) {
    return this.request(`/study/relearn/flashcard?content_id=${contentId}`);
  }

  async getQuizById(contentId) {
    return this.request(`/study/relearn/quiz?content_id=${contentId}`);
  }

  async likeContent(contentId, contentType = 'study', liked = true) {
    return this.request('/study-ai/study-ai/like', {
      method: 'POST',
      body: { content_id: contentId, content_type: contentType, liked }
    });
  }

  // Course selection endpoints
  async getAvailableSubjects() {
    return this.request('/course-selections/subjects');
  }

  async getUserSubjects() {
    return this.request('/course-selections/user-subjects');
  }

  async saveCourseSelection(courseData) {
    return this.request('/course-selections/save', {
      method: 'POST',
      body: courseData
    });
  }

  async checkUserCourseSelection() {

    return this.request('/course-selections/check-user-selection');
  }

  // Auth endpoints
  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async registerOAuthUser(userData) {
    return this.request('/auth/register-oauth', {
      method: 'POST',
      body: userData
    });
  }

  // Document endpoints
  async getDocuments() {
    return this.request('/documents/');
  }

  // File upload endpoints
  async uploadFile(file, subject, chapter) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('subject', subject);
    formData.append('chapter', chapter);

    return this.request('/upload/file', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`
      },
      body: formData
    });
  }

  // TTS Methods
  async generateTTSAudio(text, voice = 'en-IN', speed = 1.0) {
    return this.request('/tts/generate', {
      method: 'POST',
      body: { text, voice, speed }
    });
  }

  async streamTTSAudio(text, voice = 'en-IN', speed = 1.0) {
    const url = `${this.baseURL}${this.apiVersion}/tts/stream`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
        'x_user_token': this.getAuthToken()
      },
      body: JSON.stringify({ text, voice, speed })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TTS generation failed: ${response.status} - ${errorText}`);
    }

    return response.blob();
  }

  async getTTSVoices() {
    return this.request('/tts/voices');
  }

  async getTTSHealth() {
    return this.request('/tts/health');
  }

  async testTTS() {
    const url = `${this.baseURL}${this.apiVersion}/tts/test`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`,
        'x_user_token': this.getAuthToken()
      }
    });

    if (!response.ok) {
      throw new Error(`TTS test failed: ${response.status}`);
    }

    return response.arrayBuffer();
  }

  // Reading Room API methods
  async uploadPDF(file, subject) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('subject', subject);
    
    return this.request('/reading/upload-pdf', {
      method: 'POST',
      body: formData
    });
  }

  async getUserReadingSessions(subject = null) {
    const params = subject ? `?subject=${encodeURIComponent(subject)}` : '';
    return this.request(`/reading/sessions${params}`);
  }

  async getReadingSession(sessionId) {
    return this.request(`/reading/sessions/${sessionId}`);
  }

  async updateReadingProgress(sessionId, progressData) {
    return this.request(`/reading/sessions/${sessionId}/progress`, {
      method: 'PUT',
      body: progressData
    });
  }

  async getUserFiles(subject = null) {
    const params = subject ? `?subject=${encodeURIComponent(subject)}` : '';
    return this.request(`/reading/user-files${params}`);
  }

  // Notes endpoints
  async createNote(noteData) {
    return this.request('/reading/notes', {
      method: 'POST',
      body: noteData
    });
  }

  async getNotesByFile(fileUploadId) {
    return this.request(`/reading/files/${fileUploadId}/notes`);
  }

  async getAllUserNotes() {
    return this.request('/reading/notes');
  }

  // Bookmarks endpoints
  async createBookmark(bookmarkData) {
    return this.request('/reading/bookmarks', {
      method: 'POST',
      body: bookmarkData
    });
  }

  async getBookmarksByFile(fileUploadId) {
    return this.request(`/reading/files/${fileUploadId}/bookmarks`);
  }


}

export default new ApiService(); 